/**
 * Template Service
 * Service layer for template-related API operations
 */

import { apiService, ApiError } from "./api.js";
import {
  validateTemplate,
  formatTemplateForDisplay,
} from "../models/Template.js";
import {
  transformApiResponse,
  // transformTemplateForApi,
  prepareForApi,
} from "../utils/dataTransforms.js";
import {
  transformTemplateForLambda,
  transformLambdaResponseToFrontend,
  validateTemplateForLambda,
} from "../utils/templateDataTransforms.js";

/**
 * Template service class
 */
export class TemplateService {
  constructor() {
    this.apiService = apiService;
    this.basePath = "/templates";
  }

  /**
   * Fetches all templates from the API
   * @param {Object} options - Query options
   * @param {string} options.category - Filter by category
   * @param {string} options.search - Search term
   * @param {boolean} options.activeOnly - Only return active templates
   * @returns {Promise<Array>} Array of templates
   */
  async getTemplates(options = {}) {
    try {
      const params = {};

      // Add query parameters based on options
      if (options.category) {
        params.category = options.category;
      }

      if (options.search) {
        params.search = options.search;
      }

      if (options.activeOnly !== undefined) {
        params.active = options.activeOnly;
      }

      const response = await this.apiService.get(this.basePath, { params });

      // Transform API response to match our Template model
      console.log("Before Transformation: ", response.data);
      const templates = this.transformTemplatesResponse(response.data);
      console.log("After Transformation: ", templates);
      return templates;
    } catch (error) {
      console.error("Failed to fetch templates:", error);
      throw new ApiError(
        "Failed to load templates. Please try again.",
        error.statusCode || 500,
        { operation: "getTemplates", options }
      );
    }
  }

  /**
   * Fetches a single template by ID
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Template object
   */
  async getTemplate(templateId) {
    try {
      if (!templateId) {
        throw new ApiError("Template ID is required", 400);
      }

      const response = await this.apiService.get(
        `${this.basePath}/${templateId}`
      );

      // Transform API response to match our Template model
      const template = this.transformTemplateResponse(response.data);

      return template;
    } catch (error) {
      console.error(`Failed to fetch template ${templateId}:`, error);
      throw new ApiError(
        "Failed to load template. Please try again.",
        error.statusCode || 500,
        { operation: "getTemplate", templateId }
      );
    }
  }

  /**
   * Creates a new template
   * @param {Object} templateData - Template data to create
   * @returns {Promise<Object>} Created template
   */
  async createTemplate(templateData) {
    try {
      // Validate template data for Lambda compatibility
      const lambdaValidation = validateTemplateForLambda(templateData);
      if (!lambdaValidation.isValid) {
        throw new ApiError(
          `Invalid template data: ${lambdaValidation.errors.join(", ")}`,
          400,
          { validation: lambdaValidation }
        );
      }

      // Also validate with existing template validation for consistency
      const validation = validateTemplate(templateData);
      if (!validation.isValid) {
        throw new ApiError(
          `Invalid template data: ${validation.errors.join(", ")}`,
          400,
          { validation }
        );
      }

      // Transform template data for Lambda function
      console.log("Before Lambda Transformation: ", templateData);
      const lambdaData = transformTemplateForLambda(templateData);
      console.log("TransformedData: ", lambdaData);
      const lambdaBody = {
        templateData: lambdaData,
        templateAction: "new",
      };

      // Use the correct endpoint for Lambda function
      const response = await this.apiService.post(
        `${this.basePath}/new`,
        lambdaBody
      );

      // Transform Lambda response back to frontend format
      const template = transformLambdaResponseToFrontend(response.data);

      return template;
    } catch (error) {
      console.error("Failed to create template:", error);

      // Handle specific Lambda validation errors
      if (error.message && error.message.includes("Required field")) {
        throw new ApiError(error.message, 400, {
          operation: "createTemplate",
          templateData,
        });
      }

      throw new ApiError(
        "Failed to create template. Please try again.",
        error.statusCode || 500,
        { operation: "createTemplate", templateData }
      );
    }
  }

  /**
   * Creates a new template
   * @param {Object} templateData - Template data to edit
   * @param {Object} templateId - Id of the template to edit;
   * @returns {Promise<Object>} Created template
   */
  async editTemplate(templateData, templateId) {
    try {
      // Validate template data for Lambda compatibility
      const lambdaValidation = validateTemplateForLambda(templateData);
      if (!lambdaValidation.isValid) {
        throw new ApiError(
          `Invalid template data: ${lambdaValidation.errors.join(", ")}`,
          400,
          { validation: lambdaValidation }
        );
      }

      // Also validate with existing template validation for consistency
      const validation = validateTemplate(templateData);
      if (!validation.isValid) {
        throw new ApiError(
          `Invalid template data: ${validation.errors.join(", ")}`,
          400,
          { validation }
        );
      }

      // Transform template data for Lambda function
      console.log("Before Lambda Transformation: ", templateData);
      const lambdaData = transformTemplateForLambda(templateData);
      console.log("TransformedData: ", lambdaData);
      const lambdaBody = {
        templateData: lambdaData,
        templateAction: "edit",
        templateId: templateId,
      };

      // Use the correct endpoint for Lambda function
      const response = await this.apiService.post(
        `${this.basePath}/new`,
        lambdaBody
      );

      // Transform Lambda response back to frontend format
      const template = transformLambdaResponseToFrontend(response.data);

      return template;
    } catch (error) {
      console.error("Failed to create template:", error);

      // Handle specific Lambda validation errors
      if (error.message && error.message.includes("Required field")) {
        throw new ApiError(error.message, 400, {
          operation: "editTemplate",
          templateData,
        });
      }

      throw new ApiError(
        "Failed to update template. Please try again.",
        error.statusCode || 500,
        { operation: "editTemplate", templateData }
      );
    }
  }

  /**
   * Updates an existing template
   * @param {string} templateId - Template ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated template
   */
  async updateTemplate(templateId, updateData) {
    try {
      if (!templateId) {
        throw new ApiError("Template ID is required", 400);
      }

      // Validate update data
      const validation = validateTemplate(updateData);
      if (!validation.isValid) {
        throw new ApiError(
          `Invalid template data: ${validation.errors.join(", ")}`,
          400,
          { validation }
        );
      }

      // Transform update data for API
      const apiData = prepareForApi(updateData, "update");

      const response = await this.apiService.patch(
        `${this.basePath}/${templateId}`,
        apiData
      );

      // Transform API response back to our Template model
      const template = this.transformTemplateResponse(response.data);

      return template;
    } catch (error) {
      console.error(`Failed to update template ${templateId}:`, error);
      throw new ApiError(
        "Failed to update template. Please try again.",
        error.statusCode || 500,
        { operation: "updateTemplate", templateId, updateData }
      );
    }
  }

  /**
   * Deletes a template
   * @param {string} templateId - Template ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteTemplate(templateId) {
    try {
      if (!templateId) {
        throw new ApiError("Template ID is required", 400);
      }

      await this.apiService.delete(`${this.basePath}/${templateId}`);

      return true;
    } catch (error) {
      console.error(`Failed to delete template ${templateId}:`, error);
      throw new ApiError(
        "Failed to delete template. Please try again.",
        error.statusCode || 500,
        { operation: "deleteTemplate", templateId }
      );
    }
  }

  /**
   * Duplicates a template
   * @param {string} templateId - Template ID to duplicate
   * @param {Object} overrides - Properties to override in the copy
   * @returns {Promise<Object>} Duplicated template
   */
  async duplicateTemplate(templateId, overrides = {}) {
    try {
      // First, get the original template
      const originalTemplate = await this.getTemplate(templateId);

      // Create a copy with overrides
      const duplicateData = {
        ...originalTemplate,
        name: `${originalTemplate.name} (Copy)`,
        version: "1.0.0",
        ...overrides,
      };

      // Remove ID so a new one is generated
      delete duplicateData.id;
      delete duplicateData.createdAt;
      delete duplicateData.updatedAt;

      // Create the duplicate
      return await this.createTemplate(duplicateData);
    } catch (error) {
      console.error(`Failed to duplicate template ${templateId}:`, error);
      throw new ApiError(
        "Failed to duplicate template. Please try again.",
        error.statusCode || 500,
        { operation: "duplicateTemplate", templateId, overrides }
      );
    }
  }

  /**
   * Transforms API templates response to match our Template model
   * @param {*} apiData - Raw API response data
   * @returns {Array} Array of transformed templates
   */
  transformTemplatesResponse(apiData) {
    try {
      // Handle different response formats
      let templates = [];

      if (Array.isArray(apiData)) {
        templates = apiData;
      } else if (apiData && Array.isArray(apiData.templates)) {
        templates = apiData.templates;
      } else if (apiData && Array.isArray(apiData.items)) {
        templates = apiData.items;
      } else {
        // If no templates found, return empty array
        return [];
      }

      return templates.map((template) =>
        this.transformTemplateResponse(template)
      );
    } catch (error) {
      console.log("Failed to transform templates response:", error);
      return [];
    }
  }

  /**
   * Transforms a single API template response to match our Template model
   * @param {Object} apiTemplate - Raw API template data
   * @returns {Object} Transformed template
   */
  transformTemplateResponse(apiTemplate) {
    try {
      // Use the existing data transform utility if available
      if (typeof transformApiResponse === "function") {
        return transformApiResponse(apiTemplate);
      }

      // Fallback transformation
      return {
        id: apiTemplate.id || apiTemplate._id,
        name: apiTemplate.name || "Untitled Template",
        description: apiTemplate.description || "",
        version: apiTemplate.version || "1.0.0",
        isActive:
          apiTemplate.isActive !== undefined ? apiTemplate.isActive : true,
        items: apiTemplate.items || [],
        metadata: {
          estimatedTimeMinutes: 15,
          category: "general",
          tags: [],
          countries: [],
          languages: [],
          ...apiTemplate.metadata,
        },
        createdBy: apiTemplate.createdBy || null,
        createdAt: apiTemplate.createdAt || new Date().toISOString(),
        updatedAt: apiTemplate.updatedAt || new Date().toISOString(),
      };
    } catch (error) {
      console.error("Failed to transform template response:", error);
      // Return a minimal valid template structure
      return {
        id: apiTemplate?.id || "unknown",
        name: apiTemplate?.name || "Untitled Template",
        description: "",
        version: "1.0.0",
        isActive: true,
        items: [],
        metadata: {
          estimatedTimeMinutes: 15,
          category: "general",
          tags: [],
          countries: [],
          languages: [],
        },
        createdBy: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    }
  }
}

// Create and export default template service instance
export const templateService = new TemplateService();

export default templateService;
